import { AiOutlineArrowRight } from "react-icons/ai";
import "./HeaderStyle.css";
import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../config/FirebaseConfig";
import { openSupportChat, HIRE_MESSAGE } from "../SupportChat/SupportChat";
import { IoIosClose } from "react-icons/io";
import {
  BLOG_NOTICE_CONFIG_DOC,
  BLOG_NOTICE_COOLDOWN_MS,
  BLOG_NOTICE_DEFAULT_TEXTS,
  BLOG_TEXT_DELAY_MS,
  BLOG_TEXT_GAP_MS,
  BLOG_TEXT_HOLD_MS,
  BLOG_TEXT_TYPE_INTERVAL_MS,
  sanitizeBlogNoticeTexts,
} from "../../config/blogNoticeConfig";

const BLOG_NOTICE_STORAGE_KEY = "sette_blog_notice_ack";

export default function HeaderComponent() {
  const { pathname } = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveredLogin, setIsHoveredLogin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [blogNoticeTexts, setBlogNoticeTexts] = useState(() =>
    sanitizeBlogNoticeTexts(BLOG_NOTICE_DEFAULT_TEXTS),
  );
  const [blogNoticeNow, setBlogNoticeNow] = useState(() => Date.now());
  const [blogNoticeAck, setBlogNoticeAck] = useState(() => {
    if (typeof window === "undefined") {
      return 0;
    }
    try {
      const stored = window.sessionStorage.getItem(BLOG_NOTICE_STORAGE_KEY);
      return stored ? Number(stored) || 0 : 0;
    } catch (error) {
      return 0;
    }
  });
  const [notifyText, setNotifyText] = useState("");
  const blogTextDelayRef = useRef(null);
  const blogTextHoldRef = useRef(null);
  const blogTextIntervalRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadBlogNoticeTexts = async () => {
      try {
        const docRef = doc(db, "config", BLOG_NOTICE_CONFIG_DOC);
        const docSnap = await getDoc(docRef);
        if (!isMounted || !docSnap.exists()) {
          return;
        }
        const storedTexts = docSnap.data()?.phrases;
        if (!Array.isArray(storedTexts)) {
          return;
        }
        setBlogNoticeTexts(sanitizeBlogNoticeTexts(storedTexts));
      } catch (error) {
        console.error("Erro ao carregar textos do blog:", error);
      }
    };

    loadBlogNoticeTexts();

    return () => {
      isMounted = false;
    };
  }, []);

  const shouldShowBlogNotice =
    blogNoticeTexts.length > 0 &&
    (blogNoticeAck <= 0 ||
      blogNoticeNow - blogNoticeAck >= BLOG_NOTICE_COOLDOWN_MS);

  useEffect(() => {
    setBlogNoticeNow(Date.now());
    if (blogNoticeAck <= 0) {
      return undefined;
    }
    const remaining = BLOG_NOTICE_COOLDOWN_MS - (Date.now() - blogNoticeAck);
    if (remaining <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setBlogNoticeNow(Date.now());
    }, remaining);
    return () => clearTimeout(timer);
  }, [blogNoticeAck]);

  useEffect(() => {
    const clearBlogTimers = () => {
      if (blogTextDelayRef.current) {
        clearTimeout(blogTextDelayRef.current);
        blogTextDelayRef.current = null;
      }
      if (blogTextHoldRef.current) {
        clearTimeout(blogTextHoldRef.current);
        blogTextHoldRef.current = null;
      }
      if (blogTextIntervalRef.current) {
        clearInterval(blogTextIntervalRef.current);
        blogTextIntervalRef.current = null;
      }
    };

    clearBlogTimers();

    if (!shouldShowBlogNotice) {
      setNotifyText("");
      return clearBlogTimers;
    }

    setNotifyText("");
    blogTextDelayRef.current = setTimeout(() => {
      let phraseIndex = 0;
      const startTyping = () => {
        const text = blogNoticeTexts[phraseIndex];
        let index = 0;
        blogTextIntervalRef.current = setInterval(() => {
          index += 1;
          setNotifyText(text.slice(0, index));
          if (index >= text.length) {
            clearInterval(blogTextIntervalRef.current);
            blogTextIntervalRef.current = null;
            blogTextHoldRef.current = setTimeout(() => {
              startErasing(text);
            }, BLOG_TEXT_HOLD_MS);
          }
        }, BLOG_TEXT_TYPE_INTERVAL_MS);
      };

      const startErasing = (text) => {
        let eraseIndex = text.length;
        blogTextIntervalRef.current = setInterval(() => {
          eraseIndex -= 1;
          setNotifyText(text.slice(0, eraseIndex));
          if (eraseIndex <= 0) {
            clearInterval(blogTextIntervalRef.current);
            blogTextIntervalRef.current = null;
            phraseIndex = (phraseIndex + 1) % blogNoticeTexts.length;
            blogTextHoldRef.current = setTimeout(() => {
              startTyping();
            }, BLOG_TEXT_GAP_MS);
          }
        }, BLOG_TEXT_TYPE_INTERVAL_MS);
      };

      startTyping();
    }, BLOG_TEXT_DELAY_MS);

    return clearBlogTimers;
  }, [shouldShowBlogNotice, blogNoticeTexts]);

  // Esconde o balão do chat enquanto o menu mobile está aberto (ele cobria o "Assine já").
  useEffect(() => {
    document.body.classList.toggle("mobile-menu-open", menuOpen);
    return () => document.body.classList.remove("mobile-menu-open");
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const getSectionHref = (sectionId) =>
    `${pathname === "/" ? "" : "/"}#${sectionId}`;

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const handleBlogLinkClick = () => {
    const ackTimestamp = Date.now();
    setBlogNoticeAck(ackTimestamp);
    try {
      window.sessionStorage.setItem(
        BLOG_NOTICE_STORAGE_KEY,
        String(ackTimestamp),
      );
    } catch (error) {
      void error;
    }
    closeMenu();
  };

  return (
    <div className={`nav_bar ${isScrolled ? "scrolled" : ""}`}>
      <div className="frameNav">
        <span
          className="logo_se77e"
          onClick={() => {
            window.location.href = "/";
          }}
          style={{ cursor: "pointer" }}
        >
          <img
            alt="Logo da Sette Fibra"
            className="logo-desktop"
            src="../../assets/logo/SETTE-FIBRA2.png"
          />
        </span>
        <span className="links">
          <span className="links-text">
            <a className="link" href="/" onClick={closeMenu}>
              Início
            </a>
            <a
              className="link"
              href={getSectionHref("plans")}
              onClick={closeMenu}
            >
              Planos
            </a>
            <a
              className="link"
              href={getSectionHref("lancamentos")}
              onClick={closeMenu}
            >
              Lançamentos
            </a>
            <a
              className="link"
              href={getSectionHref("advantage")}
              onClick={closeMenu}
            >
              Vantagens
            </a>
            <a
              className="link"
              href={getSectionHref("cities")}
              onClick={closeMenu}
            >
              Cidades
            </a>

            <a
              className="link"
              href={getSectionHref("app")}
              onClick={closeMenu}
            >
              App
            </a>
            <span className="link-with-notification">
              <Link
                to={"/blog"}
                className="link"
                href="/blog"
                onClick={handleBlogLinkClick}
              >
                Blog
              </Link>
              {shouldShowBlogNotice && (
                <span
                  className={`blog-notify-badge ${
                    notifyText ? "expanded" : "compact"
                  }`}
                  role={notifyText ? "status" : undefined}
                  aria-live={notifyText ? "polite" : undefined}
                  aria-hidden={!notifyText}
                >
                  {notifyText}
                </span>
              )}
            </span>
            <a
              className="link"
              href="#atendimento"
              onClick={(event) => {
                event.preventDefault();
                closeMenu();
                openSupportChat();
              }}
            >
              Atendimento
            </a>
          </span>
          <span className="buttons-header">
            <button
              className="buttonAssign"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => {
                openSupportChat(HIRE_MESSAGE);
              }}
            >
              Assine já{" "}
            </button>
            <button
              className="buttonLogin"
              onMouseEnter={() => setIsHoveredLogin(true)}
              onMouseLeave={() => setIsHoveredLogin(false)}
              onClick={() => {
                window.location.href =
                  "https://ixc.se77e.com.br/central_assinante_web/login";
              }}
            >
              Central do Assinante{" "}
            </button>
          </span>
        </span>
        {!menuOpen && (
          <div className="burger-icon" onClick={toggleMenu} aria-label="Menu">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
        {menuOpen && (
          <div
            className="close-icon"
            aria-label="Close Menu"
            role="button"
            tabIndex={0}
            onClick={closeMenu}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                closeMenu();
              }
            }}
          >
            <IoIosClose size={26} className="close-icon-svg" />
          </div>
        )}
        <a className="link" href="/" onClick={closeMenu}>
          Início
        </a>
        <a className="link" href={getSectionHref("plans")} onClick={closeMenu}>
          Planos
        </a>
        <a
          className="link"
          href={getSectionHref("lancamentos")}
          onClick={closeMenu}
        >
          Lançamentos
        </a>
        <a
          className="link"
          href={getSectionHref("advantage")}
          onClick={closeMenu}
        >
          Vantagens
        </a>
        <a className="link" href={getSectionHref("cities")} onClick={closeMenu}>
          Cidades
        </a>
        <a className="link" href={getSectionHref("app")} onClick={closeMenu}>
          App
        </a>
        <span className="link-with-notification">
          <Link to={"/blog"} className="link" onClick={handleBlogLinkClick}>
            Blog
          </Link>
          {shouldShowBlogNotice && (
            <span
              className={`blog-notify-badge ${
                notifyText ? "expanded" : "compact"
              }`}
              role={notifyText ? "status" : undefined}
              aria-live={notifyText ? "polite" : undefined}
              aria-hidden={!notifyText}
            >
              {notifyText}
            </span>
          )}
        </span>
        <a
              className="link"
              href="#atendimento"
              onClick={(event) => {
                event.preventDefault();
                closeMenu();
                openSupportChat();
              }}
            >
          Atendimento
        </a>
        <div className="buttons-header-mobile">
          <button
            className="buttonAssign"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
              openSupportChat(HIRE_MESSAGE);
            }}
          >
            Assine já{" "}
            {isHovered && (
              <AiOutlineArrowRight fontSize={"1.8660812294182216vh"} />
            )}
          </button>
          <button
            className="buttonLogin"
            onMouseEnter={() => setIsHoveredLogin(true)}
            onMouseLeave={() => setIsHoveredLogin(false)}
            onClick={() => {
              window.location.href =
                "https://ixc.se77e.com.br/central_assinante_web/login";
            }}
          >
            Central do Assinante{" "}
            {isHoveredLogin && <AiOutlineArrowRight fontSize={17} />}
          </button>
        </div>
      </div>
      {menuOpen && <div className="overlay" onClick={closeMenu} />}
    </div>
  );
}
